<template>
  <div class="supplement content-wrapper">
    <div class="page-header">{{ $t('editSupplementCharge') }}</div>
    <div style="margin-bottom: 40px; margin-left: 5px;">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ name: 'HotelSupplementItemList' }">{{ $t('supplementChargeList') }}</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $t('editSupplementCharge') }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    <el-form ref="form" label-width="150px" style="width: 500px">
      <el-form-item :label="$t('name')" :required="true">
        <el-input v-model="supplementCharge.name"></el-input>
      </el-form-item>
      <el-form-item :label="$t('type')" :required="true">
        <el-select v-model="supplementCharge.supplementTypeId" :placeholder="$t('pleaseSelectType')" style="width: 100%">
          <el-option :label="supplementType.name" :value="supplementType.id" v-for="supplementType in supplementTypeList" :key="supplementType.id"></el-option>
          <el-option :label="$t('otherType')" :value="SUPPLEMENT_TYPE.OTHER"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label=" " v-if="supplementCharge.supplementTypeId === SUPPLEMENT_TYPE.OTHER" :required="true">
        <el-input v-model="supplementCharge.supplementTypeName"></el-input>
      </el-form-item>
      <el-form-item :label="$t('rate')">
        <el-row>
          <el-col :span="12">
            <el-input v-model="supplementCharge.price" type="number"></el-input>
          </el-col>
          <el-col :span="12" style="padding-left: 5px;">
            <el-select v-model="supplementCharge.priceType">
              <el-option :label="$t(`${supplementPriceType.titleKey}`)" :value="supplementPriceType.id" v-for="supplementPriceType in supplementPriceTypeList" :key="supplementPriceType.id"></el-option>
            </el-select>
          </el-col>
        </el-row>
      </el-form-item>
      <el-form-item :label="$t('unit')">
        <el-select v-model="supplementCharge.unitType" style="width: 100%">
          <el-option :label="$t(`${supplementUnitType.titleKey}`)" :value="supplementUnitType.id" v-for="supplementUnitType in supplementUnitTypeList" :key="supplementUnitType.id"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('periodOfStay')">
        <el-row>
          <el-checkbox v-model="allTheTime">{{ $t('allTheTime') }}</el-checkbox>
        </el-row>
        <el-row v-for="(period, index) in periodList" :key="index">
          <label v-if="index > 0" class="label-remove-period">
            <i class="el-icon-remove" style="color: red" @click="onRemovePeriodButtonClick(index)"></i>
          </label>
          <el-col :span="24">
            <el-date-picker size="small" :disabled="Boolean(allTheTime)"
                            v-model="periodList[index]"
                            type="daterange"
                            format="d MMM yyyy"
                            value-format="yyyy-MM-dd"
                            start-placeholder="From"
                            end-placeholder="To"
                            @change="onDatePickerChange(index)">
            </el-date-picker>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="24">
            <el-button plain type="primary" icon="el-icon-plus" @click="onAddPeriodButtonClick()" size="mini" style="width: 100%" :disabled="Boolean(allTheTime)">
              {{ $t('addPeriod') }}
            </el-button>
          </el-col>
        </el-row>
      </el-form-item>
      <el-form-item :label="$t('conditionDetail')">
        <el-input type="textarea" :rows="3" v-model="supplementCharge.conditionDetail"></el-input>
      </el-form-item>
      <el-form-item label="">
        <el-button type="primary" @click="onUpdateSupplementButtonClick">{{ $t('updateSupplementCharge') }}</el-button>
        <el-button type="default" @click="onCancelButtonClick">{{ $t('cancel') }}</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { STATUS, SUPPLEMENT_UNIT_TYPE, SUPPLEMENT_PRICE_TYPE, SUPPLEMENT_TYPE } from '@/configs/constant'
import i18nMessages from '@/translations/Hotel/ManageHotel/Supplement/ManageSupplement/Item/Edit.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import { supplementUnitTypeList, supplementPriceTypeList, getHotelIndexRoute } from '@/services/hotel-service'
import HotelSupplementApi from '@/api/hotel/supplement-api'
import LocalStorageFactory from '@/factories/local-storage-factory'
import { mapState } from 'vuex'
import { periodOverlap, getISODateFormat, preparePeriodList, getValidPeriodList } from '@/helpers/index'
import { get, some } from 'lodash'

export default {
  name: 'HotelSupplementItemEdit',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { },
  i18n: { messages: i18nMessages },
  props: {
    supplementTypeList: {
      type: Array,
      default: function () {
        return []
      }
    },
    supplementCharge: {
      type: Object,
      default: function () {
        return {
          id: '',
          name: '',
          supplementTypeId: '',
          supplementTypeName: '',
          price: 0,
          priceType: SUPPLEMENT_PRICE_TYPE.NET,
          unitType: SUPPLEMENT_UNIT_TYPE.PER_PERSON,
          conditionDetail: '',
          periodOfStayList: []
        }
      }
    }
  },
  created: async function () {
    this.isLoading = true

    if (this.activeHotelId === undefined || this.activeHotelId === '') {
      const companyDetail = LocalStorageFactory.getCompanyDetail()
      const routeName = await getHotelIndexRoute(companyDetail, this.$store)

      return this.$router.push({
        name: routeName
      })
    }

    if (this.supplementCharge.id === undefined || this.supplementCharge.id === '') {
      return this.$router.push({
        name: 'HotelSupplementItemList'
      })
    }

    this._checkIfSupplementTypeExits()

    this.getPeriodList()

    const periodOfStayList = get(this.supplementCharge, 'periodOfStayList', [])
    this.allTheTime = (periodOfStayList.length === 0)

    this.isLoading = false
  },
  computed: {
    ...mapState({
      activeHotelId: state => state.general.activeHotelId
    })
  },
  data () {
    return {
      STATUS,
      SUPPLEMENT_TYPE,
      supplementUnitTypeList,
      supplementPriceTypeList,
      allTheTime: false,
      deletedPeriod: [],
      periodList: ['']
    }
  },
  methods: {
    onCancelButtonClick: function () {
      this.$router.push({
        name: 'HotelSupplementItemList'
      })
    },
    onUpdateSupplementButtonClick: async function () {
      if (!this._validatePeriodList()) {
        return false
      }

      try {
        this.startLoading()

        const periodList = (this.allTheTime === false) ? preparePeriodList(this.periodList) : []
        const periodOfStayList = {
          periodOfStayList: periodList
        }

        const data = Object.assign({}, this.supplementCharge, periodOfStayList)
        await HotelSupplementApi.update(this.activeHotelId, this.supplementCharge.id, data)
        await HotelSupplementApi.removePeriod(this.activeHotelId, this.supplementCharge.id, this.deletedPeriod)
        this.$router.push({
          name: 'HotelSupplementItemList'
        })

        const title = this.$t('updateSupplementChargeSuccessTitle')
        const message = this.$t('updateSupplementChargeSuccessMessage')

        this.showNotificationSuccess(title, message)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    getPeriodList: function () {
      const periodOfStayList = get(this.supplementCharge, 'periodOfStayList', [])

      this.periodList = periodOfStayList.map((periodOfStay) => {
        const startDate = getISODateFormat(new Date(periodOfStay.startDate))
        const endDate = getISODateFormat(new Date(periodOfStay.endDate))
        const period = [
          startDate,
          endDate
        ]

        return period
      })

      this.periodList = (this.periodList.length === 0) ? [''] : this.periodList
    },
    onRemovePeriodButtonClick: function (index) {
      this.deletedPeriod.push(this.periodList[index])
      this.periodList.splice(index, 1)
    },
    onAddPeriodButtonClick: function () {
      this.periodList.push('')
    },
    onDatePickerChange: function (index) {
      const periodList = getValidPeriodList(this.periodList)
      const newPeriodList = preparePeriodList(periodList)

      if (periodOverlap(newPeriodList) === true) {
        const message = this.$t('alertOverlap')
        this.alertError(message)

        this.periodList[index][0] = ''
        this.periodList[index][1] = ''
      }
    },
    _validatePeriodList: function () {
      if (this.allTheTime === true) {
        return true
      }

      const periodList = getValidPeriodList(this.periodList)

      if (periodList.length < 1) {
        let message = this.$t('pleaseSelectPeriodOfStay')
        this.alertError(message)

        return false
      }

      this.periodList = [ ...periodList ]

      return true
    },
    _checkIfSupplementTypeExits: function () {
      const isSupplementTypeExits = some(this.supplementTypeList, [this.supplementTypeId])

      if (isSupplementTypeExits === false) {
        this.supplementCharge.supplementTypeId = ''
      }
    }
  }
}
</script>

<style scoped>
  .el-form > div {
    text-align: left;
  }
  .label-remove-period {
    position: absolute;
    left: -25px;
    cursor: pointer
  }
</style>
